import { buildCoverageView } from './coverageView';
import type { CoverageView } from './coverage.types';

/**
 * One account per row of the FIR-21110 state matrix.
 *
 * Shared by the specs and by the states harness, so the states design reviews
 * are exactly the states the tests pin. `NOW` is fixed: a fixture whose
 * appearance depends on the day it is opened cannot be reviewed.
 */
export const FIXTURE_NOW = '2026-09-03T00:00:00.000Z';

export const FIXTURE_COMPANY = 'Cloudspire Inc';

const cyber = (over: Record<string, unknown> = {}) => ({
  id: 'pol-cyber',
  policyNumber: 'FLY-CB-V71W2VZPZ',
  product: 'P250',
  status: 'IN FORCE',
  effectiveDate: '2026-03-01T00:00:00.000Z',
  expiresOn: '2027-03-01T00:00:00.000Z',
  limit: 1000000,
  deductible: 10000,
  totalPremium: 8420,
  endorsementCount: 2,
  isPaid: true,
  isAppSigned: true,
  isCertificateAvailable: true,
  ...over,
});

const cyberQuote = (over: Record<string, unknown> = {}) => ({
  id: 'quo-cyber',
  quoteNumber: 'QUO-CSGDJIK',
  product: 'P250',
  customerStatus: 'AVAILABLE',
  expiresOn: '2026-09-27T00:00:00.000Z',
  initialRequestData: { limit: 1000000, deductible: 10000 },
  totalPremium: 8420,
  ...over,
});

const dno = (over: Record<string, unknown> = {}) => ({
  id: 'pol-dno',
  policyNumber: 'FLY-DO-8H2K4QMR',
  product: 'P100',
  status: 'IN FORCE',
  effectiveDate: '2026-06-01T00:00:00.000Z',
  expiresOn: '2027-06-01T00:00:00.000Z',
  limit: 2000000,
  deductible: 25000,
  totalPremium: 12600,
  endorsementCount: 0,
  isPaid: true,
  isAppSigned: true,
  isCertificateAvailable: true,
  ...over,
});

export interface CoverageScenario {
  readonly id: string;
  readonly matrixId: string;
  readonly title: string;
  readonly note: string;
  readonly policies: readonly Record<string, unknown>[];
  readonly quotes: readonly Record<string, unknown>[];
}

export const COVERAGE_SCENARIOS: readonly CoverageScenario[] = [
  {
    id: 'noCoverage',
    matrixId: 'G0',
    title: 'No coverage and no quote',
    note: 'Nothing to summarise, so the tile is absent entirely -- no empty card.',
    policies: [],
    quotes: [],
  },
  {
    id: 'quoteReady',
    matrixId: 'G1',
    title: 'Quote ready to bind',
    note: 'Premium locked until expiry, bind as the single primary action.',
    policies: [],
    quotes: [cyberQuote()],
  },
  {
    id: 'quoteBlocked',
    matrixId: 'G2',
    title: 'Quote blocked on subjectivities',
    note: 'Bind is not offered; the tile says what is outstanding instead.',
    policies: [],
    quotes: [cyberQuote({ canBind: false })],
  },
  {
    id: 'quoteExpired',
    matrixId: 'G3',
    title: 'Quote expired',
    note: 'Neutral rather than urgent — there is no deadline left to miss.',
    policies: [],
    quotes: [cyberQuote({ expiresOn: '2026-08-01T00:00:00.000Z' })],
  },
  {
    id: 'awaitingPayment',
    matrixId: 'G4',
    title: 'Bound, unpaid and unsigned',
    note: 'Two outstanding decisions, both named. Coverage has not started.',
    policies: [
      // No certificate exists until the policy issues (FIR-21145).
      cyber({
        status: 'BOUND',
        isPaid: false,
        isAppSigned: false,
        isCertificateAvailable: false,
      }),
    ],
    quotes: [],
  },
  {
    id: 'preInception',
    matrixId: 'G5',
    title: 'Issued, not yet in force',
    note: 'Empty term bar, countdown to inception, nothing actionable.',
    policies: [
      cyber({
        status: 'ISSUED',
        effectiveDate: '2026-10-01T00:00:00.000Z',
        expiresOn: '2027-10-01T00:00:00.000Z',
        endorsementCount: 0,
      }),
    ],
    quotes: [],
  },
  {
    id: 'inForce',
    matrixId: 'G6',
    title: 'In force, mid-term',
    note: 'The baseline: no renewal framing, no pending decisions.',
    policies: [cyber()],
    quotes: [],
  },
  {
    id: 'renewalDue',
    matrixId: 'G7',
    title: 'Renewal window open, no quote yet',
    note: 'The churn-risk state the prototype had no path for.',
    policies: [cyber({ expiresOn: '2026-10-20T00:00:00.000Z' })],
    quotes: [],
  },
  {
    id: 'renewalQuoted',
    matrixId: 'G8',
    title: 'In force with a renewal quote ready',
    note: 'The renewal attaches as a band, with a delta from the real premium.',
    policies: [cyber({ expiresOn: '2026-10-20T00:00:00.000Z' })],
    quotes: [
      cyberQuote({
        id: 'quo-renewal',
        quoteNumber: 'REN-CSGDJIK',
        isRenewal: true,
        totalPremium: 9180,
        expiresOn: '2026-10-06T00:00:00.000Z',
      }),
    ],
  },
  {
    id: 'renewalBound',
    matrixId: 'G9',
    title: 'In force with the next term already bound',
    note: 'One continuous story rather than two unrelated policies.',
    policies: [
      cyber({ expiresOn: '2026-10-20T00:00:00.000Z' }),
      cyber({
        id: 'pol-cyber-next',
        policyNumber: 'FLY-CB-NEXT',
        status: 'ISSUED',
        effectiveDate: '2026-10-20T00:00:00.000Z',
        expiresOn: '2027-10-20T00:00:00.000Z',
        endorsementCount: 0,
      }),
    ],
    quotes: [],
  },
  {
    id: 'lapsed',
    matrixId: 'G10',
    title: 'Expired and not renewed',
    note: 'The gap is stated plainly and dated, with claims still reachable.',
    policies: [
      cyber({
        effectiveDate: '2025-03-01T00:00:00.000Z',
        expiresOn: '2026-03-01T00:00:00.000Z',
      }),
    ],
    quotes: [],
  },
  {
    id: 'cancelled',
    matrixId: 'G11',
    title: 'Cancelled mid-term',
    note: 'Cancelled stays cancelled even with term dates still running.',
    policies: [cyber({ status: 'CANCELLED' })],
    quotes: [],
  },
  {
    id: 'multiplePolicies',
    matrixId: 'A2',
    title: 'Two policies — Cyber and D&O',
    note: 'One line leads by rank; the other becomes a scannable row.',
    policies: [dno(), cyber()],
    quotes: [],
  },
  {
    id: 'policyPlusNewQuote',
    matrixId: 'A3',
    title: 'In-force policy plus a quote for another product',
    note: 'The quote row is badged and counted as awaiting a decision.',
    policies: [cyber()],
    quotes: [
      cyberQuote({
        id: 'quo-dno',
        quoteNumber: 'QUO-DO-4471',
        product: 'P100',
        initialRequestData: { limit: 2000000, deductible: 25000 },
        totalPremium: 12600,
        expiresOn: '2026-10-04T00:00:00.000Z',
      }),
    ],
  },
  {
    id: 'quoteOptions',
    matrixId: 'A4',
    title: 'Several quote options for one product',
    note: 'One decision, not three. Only one of them can ever be bound.',
    policies: [],
    quotes: [
      cyberQuote({ id: 'quo-a', quoteNumber: 'QUO-A' }),
      cyberQuote({
        id: 'quo-b',
        quoteNumber: 'QUO-B',
        initialRequestData: { limit: 2000000, deductible: 25000 },
        totalPremium: 11940,
      }),
      cyberQuote({
        id: 'quo-c',
        quoteNumber: 'QUO-C',
        initialRequestData: { limit: 500000, deductible: 5000 },
        totalPremium: 6100,
      }),
    ],
  },
  {
    id: 'partialGap',
    matrixId: 'A5',
    title: 'One product in force, another lapsed',
    note: 'The gap stays visible instead of being buried under live coverage.',
    policies: [
      cyber(),
      dno({
        effectiveDate: '2025-01-01T00:00:00.000Z',
        expiresOn: '2026-01-01T00:00:00.000Z',
      }),
    ],
    quotes: [],
  },
  {
    id: 'unknownStatus',
    matrixId: '—',
    title: 'A status we do not recognise',
    note: 'Renders neutrally rather than throwing, which is what it used to do.',
    policies: [cyber({ status: 'REFERRED_TO_UW' })],
    quotes: [],
  },
];

export const viewForScenario = (scenario: CoverageScenario): CoverageView =>
  buildCoverageView({
    policies: scenario.policies,
    quotes: scenario.quotes,
    now: FIXTURE_NOW,
  });
